import { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

const TONES = [
  { v: "cordiale", label: "Cordiale-professionale" },
  { v: "formale", label: "Formale e professionale" },
];

const DAYS = [
  { v: 0, label: "Lun" },
  { v: 1, label: "Mar" },
  { v: 2, label: "Mer" },
  { v: 3, label: "Gio" },
  { v: 4, label: "Ven" },
  { v: 5, label: "Sab" },
  { v: 6, label: "Dom" },
];

const TIMEZONES = ["Europe/Rome", "Europe/Madrid", "Europe/Paris", "Europe/Berlin", "Europe/London", "UTC"];

export default function Settings() {
  const { reloadSettings } = useOutletContext();
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const { data } = await api.get("/settings");
    setS(data);
  };

  useEffect(() => {
    load();
  }, []);

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/settings", s);
      toast.success("Impostazioni salvate");
      reloadSettings && reloadSettings();
    } catch (err) {
      console.error("Settings: failed to save", err?.message || err);
      toast.error("Errore salvataggio");
    } finally {
      setBusy(false);
    }
  };

  if (!s) return <div className="text-sm text-[#8C8B8A]">Caricamento…</div>;

  // Ensure schedule_rules exist for all 7 days, fallback from legacy fields
  const existingRules = Array.isArray(s.schedule_rules) ? s.schedule_rules : [];
  const rulesByDay = {};
  for (const r of existingRules) rulesByDay[r.weekday] = r;
  const rules = DAYS.map((d) => {
    const r = rulesByDay[d.v];
    if (r) return { weekday: d.v, enabled: r.enabled ?? true, all_day: !!r.all_day, start: r.start || "09:00", end: r.end || "19:00" };
    const isWeekend = d.v === 5 || d.v === 6;
    return { weekday: d.v, enabled: true, all_day: isWeekend, start: "09:00", end: "19:00" };
  });
  const updateRule = (weekday, patch) => {
    const cur = rules;
    const next = cur.map((r) => (r.weekday === weekday ? { ...r, ...patch } : r));
    setS({ ...s, schedule_rules: next });
  };
  const applyPreset = (preset) => {
    let next;
    if (preset === "weekdays-9-19") {
      next = DAYS.map((d) => ({
        weekday: d.v,
        enabled: true,
        all_day: d.v === 5 || d.v === 6,
        start: "09:00",
        end: "19:00",
      }));
    } else if (preset === "always") {
      next = DAYS.map((d) => ({ weekday: d.v, enabled: true, all_day: true, start: "00:00", end: "23:59" }));
    } else if (preset === "weekdays-only") {
      next = DAYS.map((d) => ({
        weekday: d.v,
        enabled: d.v <= 4,
        all_day: false,
        start: "09:00",
        end: "19:00",
      }));
    }
    if (next) setS({ ...s, schedule_rules: next });
  };

  return (
    <div className="space-y-10 max-w-3xl">
      <div>
        <div className="label-mono">Configurazione</div>
        <h1 className="font-display text-4xl mt-2 font-light">Impostazioni Segretaria</h1>
      </div>

      {/* CORE */}
      <Section title="Stile risposta">
        <Field label="Tono">
          <Select value={s.tone} onValueChange={(v) => setS({ ...s, tone: v })}>
            <SelectTrigger data-testid="settings-tone-select" className="border-[#E5E4E0]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {TONES.map((t) => (
                <SelectItem key={t.v} value={t.v}>{t.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
        <Field label="Firma email">
          <Textarea
            data-testid="settings-signature-textarea"
            rows={3}
            value={s.signature || ""}
            onChange={(e) => setS({ ...s, signature: e.target.value })}
            className="border-[#E5E4E0] bg-white focus:border-[#2C4C3B] focus:ring-0 font-mono text-sm"
          />
        </Field>
        <Field label="Istruzioni personalizzate (opzionale)">
          <Textarea
            data-testid="settings-prompt-textarea"
            rows={5}
            value={s.system_prompt || ""}
            onChange={(e) => setS({ ...s, system_prompt: e.target.value })}
            placeholder="Es: Per richieste su immobili in vendita, specifica sempre che è disponibile la planimetria a richiesta."
            className="border-[#E5E4E0] bg-white focus:border-[#2C4C3B] focus:ring-0 font-mono text-sm"
          />
          <p className="text-xs text-[#8C8B8A] mt-1">
            Queste istruzioni verranno aggiunte al system prompt della Segretaria AI.
          </p>
        </Field>
      </Section>

      {/* BEHAVIOR */}
      <Section title="Comportamento">
        <ToggleRow
          label="Invio automatico"
          description="Se attivato, le risposte vengono inviate senza approvazione. Se disattivato, vengono salvate come bozze da approvare manualmente."
          checked={!!s.auto_send}
          onChange={(v) => setS({ ...s, auto_send: v })}
          testid="settings-autosend-switch"
        />
        <ToggleRow
          label="Allega link foto degli immobili"
          description="Se attivato, in coda alle risposte vengono inseriti i link delle foto degli immobili identificati (max 6 per immobile). Se disattivato, solo descrizione testuale + link scheda annuncio."
          checked={s.attach_property_photos !== false}
          onChange={(v) => setS({ ...s, attach_property_photos: v })}
          testid="settings-attach-photos-switch"
        />
        <ToggleRow
          label="Notifica per ogni risposta inviata"
          checked={!!s.notify_on_reply}
          onChange={(v) => setS({ ...s, notify_on_reply: v })}
          testid="settings-notify-reply-switch"
        />
        <ToggleRow
          label="Notifica per ogni resoconto 4 ore"
          checked={!!s.notify_on_report}
          onChange={(v) => setS({ ...s, notify_on_report: v })}
          testid="settings-notify-report-switch"
        />
        <ToggleRow
          label="Bandierina sulle email gestite (IMAP)"
          description="Dopo la risposta, applica al messaggio originale su Gmail/Outlook le bandierine standard (stella + freccia 'Risposto') e un'etichetta personalizzata. Su Gmail compare come Label, su Outlook come Categoria — puoi assegnarle un colore dalle impostazioni del tuo client."
          checked={!!s.imap_flag_replied}
          onChange={(v) => setS({ ...s, imap_flag_replied: v })}
          testid="settings-imap-flag-switch"
        />
        {s.imap_flag_replied && (
          <Field label="Nome etichetta / categoria">
            <Input
              data-testid="settings-imap-label-input"
              value={s.imap_label || "Segretaria-AI"}
              onChange={(e) => setS({ ...s, imap_label: e.target.value })}
              placeholder="Segretaria-AI"
              className="border-[#E5E4E0] bg-transparent focus:border-[#2C4C3B] focus:ring-0 font-mono"
            />
            <p className="text-xs text-[#8C8B8A] mt-1">
              Niente spazi: usa trattini o underscore. L&apos;etichetta apparirà sull&apos;email originale nel tuo client (Gmail/Outlook).
            </p>
          </Field>
        )}
      </Section>

      {/* SCHEDULE */}
      <Section title="Programmazione">
        <ToggleRow
          label="Attivazione programmata"
          description="Lavora secondo i tuoi orari: per ogni giorno scegli se attivare la Segretaria, se farla operare 24 ore o solo in una fascia oraria. Tipicamente Sab/Dom 24h e Lun–Ven solo in alcuni orari."
          checked={!!s.schedule_enabled}
          onChange={(v) => setS({ ...s, schedule_enabled: v })}
          testid="settings-schedule-switch"
        />

        {s.schedule_enabled && (
          <>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                data-testid="settings-preset-weekdays-9-19"
                onClick={() => applyPreset("weekdays-9-19")}
                className="px-3 py-2 text-[10px] font-mono uppercase tracking-[0.15em] border border-[#E5E4E0] bg-white text-[#5C5B5A] hover:bg-[#F0EFEA]"
              >
                Lun–Ven 9–19 · Sab/Dom 24h
              </button>
              <button
                type="button"
                data-testid="settings-preset-always"
                onClick={() => applyPreset("always")}
                className="px-3 py-2 text-[10px] font-mono uppercase tracking-[0.15em] border border-[#E5E4E0] bg-white text-[#5C5B5A] hover:bg-[#F0EFEA]"
              >
                Sempre attiva 24/7
              </button>
              <button
                type="button"
                data-testid="settings-preset-weekdays-only"
                onClick={() => applyPreset("weekdays-only")}
                className="px-3 py-2 text-[10px] font-mono uppercase tracking-[0.15em] border border-[#E5E4E0] bg-white text-[#5C5B5A] hover:bg-[#F0EFEA]"
              >
                Solo Lun–Ven 9–19
              </button>
            </div>

            <div className="border border-[#E5E4E0] divide-y divide-[#E5E4E0]">
              {rules.map((r) => {
                const dayLabel = DAYS.find((d) => d.v === r.weekday)?.label || r.weekday;
                return (
                  <div
                    key={r.weekday}
                    data-testid={`settings-rule-${r.weekday}`}
                    className="grid grid-cols-12 items-center gap-3 px-4 py-3 bg-white"
                  >
                    <div className="col-span-2 font-mono text-xs uppercase tracking-[0.15em] text-[#1C1B1A]">
                      {dayLabel}
                    </div>
                    <div className="col-span-3 flex items-center gap-2">
                      <Switch
                        data-testid={`settings-rule-enabled-${r.weekday}`}
                        checked={r.enabled}
                        onCheckedChange={(v) => updateRule(r.weekday, { enabled: v })}
                        className="data-[state=checked]:bg-[#2C4C3B]"
                      />
                      <span className="text-xs text-[#5C5B5A]">
                        {r.enabled ? "Attiva" : "Disattiva"}
                      </span>
                    </div>
                    <div className="col-span-3 flex items-center gap-2">
                      <Switch
                        data-testid={`settings-rule-allday-${r.weekday}`}
                        checked={r.all_day}
                        disabled={!r.enabled}
                        onCheckedChange={(v) => updateRule(r.weekday, { all_day: v })}
                        className="data-[state=checked]:bg-[#2C4C3B]"
                      />
                      <span className="text-xs text-[#5C5B5A]">24 ore</span>
                    </div>
                    <div className="col-span-4 flex items-center gap-2">
                      <Input
                        type="time"
                        data-testid={`settings-rule-start-${r.weekday}`}
                        value={r.start}
                        disabled={!r.enabled || r.all_day}
                        onChange={(e) => updateRule(r.weekday, { start: e.target.value })}
                        className="h-9 border-[#E5E4E0] bg-transparent focus:border-[#2C4C3B] focus:ring-0 disabled:opacity-40"
                      />
                      <span className="text-xs text-[#8C8B8A]">→</span>
                      <Input
                        type="time"
                        data-testid={`settings-rule-end-${r.weekday}`}
                        value={r.end}
                        disabled={!r.enabled || r.all_day}
                        onChange={(e) => updateRule(r.weekday, { end: e.target.value })}
                        className="h-9 border-[#E5E4E0] bg-transparent focus:border-[#2C4C3B] focus:ring-0 disabled:opacity-40"
                      />
                    </div>
                  </div>
                );
              })}
            </div>

            <Field label="Fuso orario">
              <Select value={s.timezone} onValueChange={(v) => setS({ ...s, timezone: v })}>
                <SelectTrigger data-testid="settings-tz-select" className="border-[#E5E4E0]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TIMEZONES.map((t) => (
                    <SelectItem key={t} value={t}>{t}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          </>
        )}
      </Section>

      <div className="sticky bottom-0 bg-[#F9F8F6] pt-4 pb-2 -mx-6 px-6 border-t border-[#E5E4E0]">
        <Button
          onClick={save}
          disabled={busy}
          data-testid="settings-save-button"
          className="bg-[#2C4C3B] hover:bg-[#1E3629] text-white font-mono uppercase text-xs tracking-[0.2em] px-8 py-6"
        >
          {busy ? "Salvataggio…" : "Salva impostazioni"}
        </Button>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="border border-[#E5E4E0] bg-white p-6 space-y-5">
      <h3 className="font-display text-2xl font-light border-b border-[#E5E4E0] pb-3">{title}</h3>
      {children}
    </section>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="label-mono mb-2 block">{label}</Label>
      {children}
    </div>
  );
}

function ToggleRow({ label, description, checked, onChange, testid }) {
  return (
    <div className="flex items-start justify-between gap-4 py-2 border-b border-[#E5E4E0] last:border-0">
      <div>
        <div className="text-sm text-[#1C1B1A]">{label}</div>
        {description && <div className="text-xs text-[#8C8B8A] mt-1 max-w-md">{description}</div>}
      </div>
      <Switch
        data-testid={testid}
        checked={checked}
        onCheckedChange={onChange}
        className="data-[state=checked]:bg-[#2C4C3B]"
      />
    </div>
  );
}
